import { httpClient } from '../httpClient/httpClient';
import { ColorType } from './metadata/colorsApi';
import {
  AutoUpdateResponse,
  AutoUpdateRequestWithData,
} from './autoUpdateTypes';
import { Metric } from './metadata/dimensionGroupsApi';
import { CompositionHistoryEvent, EntityHistory } from '../historyTypes';

export interface CompositionData {
  id: number;
  text: string;
  value?: string;
}

export interface CompositionMaterialData {
  id: number;
  text: string;
  code: string;
  articleTextField: string;
  certificationText: string;
}

export interface Color {
  code: string;
  name: string;
  description: string;
  hex: string | null;
  colorType: ColorType | null;
}

export interface DimensionValue {
  id: number;
  label: string;
  identifier: string | null;
  value: number;
  metric: Metric;
}

export interface CompositionDimensionGroup {
  id: number;
  name: string;
  fileIdentifier?: string;
  visualFileIdentifier?: string;
  dimensions: DimensionValue[];
  description: string;
}

export interface Composition {
  materials: CompositionMaterial[];
  weight: number;
  id: number;
  dimensionGroup: CompositionDimensionGroup | null;
  archived: string | null;
  note: string | null;
  fileId?: number;
  fileIdentifier: string | null;
  folderId: number;
  articleProductGroupId: number | null;
}

export interface CompositionMaterial {
  id: number;
  material: CompositionMaterialData;
  qualities: CompositionData[];
  techniques: CompositionData[];
  treatments: CompositionData[];
  colors: Color[];
  weight: number;
  note: string;
}

export interface QualityValueData {
  id: number;
  value: string;
}
export interface ColorData {
  code: string;
  name: string;
  description: string;
  hex: string | null;
  colorType: ColorType;
}
export interface CreateCompositionMaterialRequest {
  materialId: number | null;
  qualities: QualityValueData[];
  techniqueIds: number[] | null | undefined;
  treatmentIds: number[] | null | undefined;
  colors: ColorData[];
  weight: number;
  note: string;
}
export type UpdateCompositionMaterialRequest = CreateCompositionMaterialRequest;

export interface UpdateCompositionTreatmentsRequest {
  treatmentIds: number[] | null;
}

export interface DimensionData {
  id: number;
  value: number;
}

export interface CompositionCell {
  x: number;
  y: number;
  id: number;
  colorCodes: string[];
}

interface CopyCompositionRequest {
  compositionId: number;
  compositionGroupId: number;
}

export interface SupplierCompositionData {
  variantId: number;
  supplierId: number;
  branchId: number;
  cellX?: number;
  cellY?: number;
}

export interface AutoUpdateDimensionRequest {
  value: number;
}
export interface AutoUpdateDimensionAdditionalData {
  dimensionId: number;
}

export const compositionApi = {
  getComposition: (compositionId: number) =>
    httpClient.get<Composition>(`compositions/${compositionId}`),
  addComposition: (compositionGroupId: number) =>
    httpClient.post<Composition>(`compositions/${compositionGroupId}`),
  updateFile: (id: number, fileId: number) =>
    httpClient.put(`/compositions/${id}/file/${fileId}`),
  deleteFile: (id: number) => httpClient.delete(`/compositions/${id}/file`),
  removeComposition: (compositionId: number) =>
    httpClient.delete(`compositions/${compositionId}`),
  createCompositionMaterial: (
    compositionId: number,
    request: CreateCompositionMaterialRequest,
  ) =>
    httpClient.post<CompositionMaterial>(
      `compositions/${compositionId}/material`,
      { data: request },
    ),
  updateCompositionMaterial: (
    compositionId: number,
    request: UpdateCompositionMaterialRequest,
  ) =>
    httpClient.put<CompositionMaterial>(
      `compositions/${compositionId}/material`,
      { data: request },
    ),
  updateCompositionTreatments: (
    compositionId: number,
    request: UpdateCompositionTreatmentsRequest,
  ) =>
    httpClient.put<CompositionMaterial>(
      `compositions/${compositionId}/material/treatments`,
      { data: request },
    ),
  getCompositionMaterial: (materialCompositionId: number) =>
    httpClient.get<CompositionMaterial>(
      `compositions/${materialCompositionId}/material`,
    ),
  removeCompositionMaterial: (materialCompositionId: number) =>
    httpClient.delete(`compositions/${materialCompositionId}/material`),
  copyComposition: (fromId: number, request: CopyCompositionRequest) =>
    httpClient.post<Composition>(`compositions/${fromId}/copy`, {
      data: request,
    }),
  archiveComposition: (id: number) =>
    httpClient.put<Composition>(`compositions/${id}/archive`),
  restoreComposition: (id: number) =>
    httpClient.put<Composition>(`compositions/${id}/restore`),
  setDimensionGroup: (
    compositionId: number,
    request: { dimensionIds: number[]; metric: Metric },
  ) =>
    httpClient.put<CompositionDimensionGroup>(
      `compositions/${compositionId}/dimension-group`,
      {
        data: request,
      },
    ),
  setNote: (compositionId: number, request: { note: string }) =>
    httpClient.put(`compositions/${compositionId}/note`, {
      data: request,
    }),
  autoUpdateDimension: (
    request: AutoUpdateRequestWithData<
      AutoUpdateDimensionRequest,
      AutoUpdateDimensionAdditionalData
    >,
    id: number,
  ) =>
    httpClient.put<AutoUpdateResponse<DimensionData>>(
      `compositions/${id}/dimensions/auto-update`,
      { data: request },
    ),
  getHistory: (id: number) =>
    httpClient.get<EntityHistory<CompositionHistoryEvent>[]>(
      `compositions/${id}/history`,
    ),
};
